import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Upload, Check, Globe, Lock, Mail, AlertTriangle, Trash2, Bell, BellOff, Sun, Moon } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { PRESET_AVATARS, DEFAULT_AVATAR, ADMIN_AVATAR } from "../lib/avatars";
import { toast } from "sonner";

export default function Settings() {
  const navigate = useNavigate();
  const { user, refresh, logout } = useAuth();
  const { t, lang, setLang, languages } = useI18n();
  const [nickname, setNickname] = useState("");
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem("bl_theme") === "light" ? "light" : "dark"; } catch { return "dark"; }
  });
  const applyTheme = (mode) => {
    setTheme(mode);
    const root = document.documentElement;
    if (mode === "light") root.classList.add("light"); else root.classList.remove("light");
    try { localStorage.setItem("bl_theme", mode); } catch { /* */ }
  };
  const [selected, setSelected] = useState(null);
  const [customAvatar, setCustomAvatar] = useState(null);
  const [saving, setSaving] = useState(false);
  const [emailPin, setEmailPin] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [changingEmail, setChangingEmail] = useState(false);
  const [currentPin, setCurrentPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [changingPin, setChangingPin] = useState(false);

  const onlyDigits = (v) => v.replace(/\D/g, "").slice(0, 6);

  const changePin = async () => {
    if (newPin !== confirmPin) {
      toast.error(t("settings.pin_mismatch"));
      return;
    }
    if (!/^\d+$/.test(newPin) || (newPin.length !== 4 && newPin.length !== 6)) {
      toast.error(t("settings.pin_invalid"));
      return;
    }
    setChangingPin(true);
    try {
      await api.post("/auth/change-pin", { current_pin: currentPin, new_pin: newPin });
      toast.success(t("settings.pin_changed"));
      setCurrentPin("");
      setNewPin("");
      setConfirmPin("");
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "";
      if (status === 400 && detail.includes("actuel")) toast.error(t("settings.pin_current_wrong"));
      else if (status === 400) toast.error(t("settings.pin_invalid"));
      else toast.error(t("settings.pin_error"));
    } finally {
      setChangingPin(false);
    }
  };

  useEffect(() => {
    if (user) {
      setNickname(user.nickname || "");
      setSelected(user.picture || DEFAULT_AVATAR);
      setCustomAvatar(user.custom_avatar || null);
    }
  }, [user]);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error(t("settings.image_too_big"));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setCustomAvatar(reader.result);
      setSelected(null);
    };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profile", {
        nickname: nickname.trim() || user.name,
        picture: selected,
        custom_avatar: customAvatar || "",
      });
      await refresh();
      toast.success(t("settings.saved"));
      navigate("/dashboard");
    } catch {
      toast.error(t("settings.save_error"));
    } finally {
      setSaving(false);
    }
  };

  // ── Notifications push ──
  const pushSupported = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
  const [pushState, setPushState] = useState("loading"); // loading | off | on | denied | unsupported
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => {
    if (!pushSupported) { setPushState("unsupported"); return; }
    if (Notification.permission === "denied") { setPushState("denied"); return; }
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => setPushState(sub ? "on" : "off"))
      .catch(() => setPushState("off"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const urlB64ToUint8 = (base64) => {
    const padding = "=".repeat((4 - (base64.length % 4)) % 4);
    const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = window.atob(b64);
    return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
  };

  const enablePush = async () => {
    setPushBusy(true);
    try {
      const reg = await navigator.serviceWorker.register("/sw.js");
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { setPushState(perm === "denied" ? "denied" : "off"); return; }
      const { data } = await api.get("/push/vapid-public-key");
      if (!data.key) { toast.error(t("push.error")); return; }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8(data.key),
      });
      await api.post("/push/subscribe", sub.toJSON());
      setPushState("on");
      toast.success(t("push.enabled"));
    } catch {
      toast.error(t("push.error"));
    } finally { setPushBusy(false); }
  };

  const disablePush = async () => {
    setPushBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.post("/push/unsubscribe", sub.toJSON()).catch(() => {});
        await sub.unsubscribe();
      }
      setPushState("off");
      toast.success(t("push.disabled"));
    } catch { toast.error(t("push.error")); }
    finally { setPushBusy(false); }
  };

  const [dangerStep, setDangerStep] = useState(0); // 0 fermé | 1 PIN | 2 confirmation
  const [deletePin, setDeletePin] = useState("");
  const [deleting, setDeleting] = useState(false);

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete("/auth/account", { data: { pin: deletePin } });
      toast.success(t("danger.deleted"));
      logout();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("danger.error"));
      setDeleting(false);
    }
  };

  const changeEmail = async () => {
    if (!newEmail.trim() || !emailPin) return;
    setChangingEmail(true);
    try {
      await api.post("/auth/change-email", { current_pin: emailPin, new_email: newEmail.trim() });
      toast.success(t("reset.email_saved"));
      setEmailPin(""); setNewEmail("");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("reset.email_error"));
    } finally {
      setChangingEmail(false);
    }
  };

  const currentAvatar = customAvatar || selected || DEFAULT_AVATAR;

  return (
    <div className="min-h-screen bg-[#0A0A0A] grain text-white">
      <header className="sticky top-0 z-20 glass-header">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-3">
          <button data-testid="settings-back" onClick={() => navigate("/dashboard")} className="p-2 hover:bg-zinc-900 rounded-md">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <span className="font-heading font-black text-xl uppercase tracking-tight">{t("settings.title")}</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12 space-y-12">
        <div>
          <h2 className="font-heading font-black text-2xl uppercase tracking-tight mb-1">{t("appearance.title")}</h2>
          <p className="text-sm text-zinc-500 mb-4">{t("appearance.desc")}</p>
          <div className="flex gap-3 max-w-sm">
            <button onClick={() => applyTheme("dark")}
              style={{ ["--glow"]: "34 211 238" }}
              className={`flex-1 glass-tile flex items-center justify-center gap-2 px-4 py-3 font-bold uppercase tracking-wide text-sm ${theme === "dark" ? "!border-cyan-400" : ""}`}>
              <Moon className="h-4 w-4 text-cyan-400" /> {t("appearance.dark")}
            </button>
            <button onClick={() => applyTheme("light")}
              style={{ ["--glow"]: "253 224 71" }}
              className={`flex-1 glass-tile flex items-center justify-center gap-2 px-4 py-3 font-bold uppercase tracking-wide text-sm ${theme === "light" ? "!border-cyan-400" : ""}`}>
              <Sun className="h-4 w-4 text-amber-400" /> {t("appearance.light")}
            </button>
          </div>
        </div>

        <div>
          <div className="text-cyan-400 text-xs uppercase tracking-[0.3em] mb-2">{t("settings.identity")}</div>
          <h1 className="font-heading font-black text-5xl uppercase tracking-tight">{t("settings.your_id")}</h1>
        </div>

        <div className="flex items-center gap-6">
          <div className="h-24 w-24 rounded-full bg-zinc-900 border-2 border-cyan-400/40 overflow-hidden flex items-center justify-center">
            <img src={currentAvatar} alt="" className="h-full w-full object-cover" />
          </div>
          <div className="flex-1">
            <label className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2 block">{t("settings.nickname")}</label>
            <input
              data-testid="nickname-input"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder={t("settings.nickname_placeholder")}
              className="w-full bg-zinc-950 border border-zinc-800 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 outline-none rounded-md px-4 py-3 text-lg"
            />
          </div>
        </div>

        <div>
          <h2 className="font-heading font-black text-2xl uppercase tracking-tight mb-4">{t("settings.preset_avatars")}</h2>
          <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
            {(user?.is_admin ? [...PRESET_AVATARS, ADMIN_AVATAR] : PRESET_AVATARS).map((a, i) => (
              <button
                key={a.id}
                data-testid={`preset-avatar-${i}`}
                onClick={() => { setSelected(a.src); setCustomAvatar(null); }}
                title={a.label}
                className={`relative aspect-square rounded-md overflow-hidden bg-zinc-900 border-2 transition-all ${selected === a.src && !customAvatar ? (a.id === "admin" ? "border-[#FDE047]" : "border-cyan-400") : a.id === "admin" ? "border-[#FDE047]/40 hover:border-[#FDE047]/70" : "border-zinc-800 hover:border-zinc-600"}`}
              >
                <img src={a.src} alt={a.label} className="h-full w-full object-cover" />
                {selected === a.src && !customAvatar && (
                  <div className={`absolute top-1 right-1 ${a.id === "admin" ? "bg-[#FDE047]" : "bg-cyan-400"} text-black rounded-full p-0.5`}>
                    <Check className="h-3 w-3" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        <div>
          <h2 className="font-heading font-black text-2xl uppercase tracking-tight mb-4">{t("settings.custom_upload")}</h2>
          <label className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 px-5 py-3 rounded-md cursor-pointer transition-colors">
            <Upload className="h-4 w-4 text-cyan-400" />
            <span className="uppercase text-sm tracking-wide">{t("settings.upload_btn")}</span>
            <input data-testid="avatar-upload" type="file" accept="image/*" onChange={onFile} className="hidden" />
          </label>
          {customAvatar && (
            <div className="mt-4 text-sm text-cyan-400" data-testid="custom-avatar-selected">{t("settings.custom_selected")}</div>
          )}
        </div>

        <div>
          <h2 className="font-heading font-black text-2xl uppercase tracking-tight mb-4 flex items-center gap-2">
            <Globe className="h-5 w-5 text-cyan-400" /> {t("settings.language")}
          </h2>
          <div className="flex flex-wrap gap-2">
            {languages.map((l) => (
              <button
                key={l.code}
                data-testid={`lang-${l.code}`}
                onClick={() => setLang(l.code)}
                className={`px-5 py-2.5 rounded-full border-2 text-sm uppercase tracking-wider font-bold transition-all ${lang === l.code ? "border-cyan-400 bg-cyan-400/10 text-cyan-400" : "border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500"}`}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>

        <div className="pt-6 border-t border-zinc-800">
          <h2 className="font-heading font-black text-2xl uppercase tracking-tight mb-1 flex items-center gap-2">
            <Mail className="h-5 w-5 text-cyan-400" /> {t("reset.change_email")}
          </h2>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">
            {t("reset.current_email")} : <span className="text-zinc-300 normal-case tracking-normal">{user?.email || "—"}</span>
          </div>
          <div className="grid gap-4 max-w-sm">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2 block">{t("reset.new_email")}</label>
              <input type="email" inputMode="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 outline-none rounded-md px-4 py-3" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2 block">{t("reset.current_pin")}</label>
              <input type="password" inputMode="numeric" value={emailPin} onChange={(e) => setEmailPin(onlyDigits(e.target.value))}
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 outline-none rounded-md px-4 py-3 text-lg tracking-[0.3em]" />
            </div>
            <button disabled={changingEmail || !newEmail.trim() || !emailPin} onClick={changeEmail}
              className="justify-self-start inline-flex items-center gap-2 bg-zinc-100 text-black hover:bg-white font-bold uppercase tracking-wide px-6 py-3 rounded-md disabled:opacity-50 disabled:pointer-events-none transition-colors">
              <Mail className="h-4 w-4" /> {changingEmail ? t("common.saving") : t("reset.save")}
            </button>
          </div>
        </div>

        <div className="pt-6 border-t border-zinc-800">
          <h2 className="font-heading font-black text-2xl uppercase tracking-tight mb-1 flex items-center gap-2">
            <Lock className="h-5 w-5 text-cyan-400" /> {t("settings.change_pin")}
          </h2>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">{t("settings.security")}</div>
          <div className="grid gap-4 max-w-sm">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2 block">{t("settings.current_pin")}</label>
              <input
                data-testid="current-pin-input"
                type="password"
                inputMode="numeric"
                autoComplete="current-password"
                value={currentPin}
                onChange={(e) => setCurrentPin(onlyDigits(e.target.value))}
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 outline-none rounded-md px-4 py-3 text-lg tracking-[0.3em]"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2 block">
                {t("settings.new_pin")} <span className="text-zinc-600 lowercase tracking-normal">({t("settings.pin_hint")})</span>
              </label>
              <input
                data-testid="new-pin-input"
                type="password"
                inputMode="numeric"
                autoComplete="new-password"
                value={newPin}
                onChange={(e) => setNewPin(onlyDigits(e.target.value))}
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 outline-none rounded-md px-4 py-3 text-lg tracking-[0.3em]"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2 block">{t("settings.confirm_pin")}</label>
              <input
                data-testid="confirm-pin-input"
                type="password"
                inputMode="numeric"
                autoComplete="new-password"
                value={confirmPin}
                onChange={(e) => setConfirmPin(onlyDigits(e.target.value))}
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 outline-none rounded-md px-4 py-3 text-lg tracking-[0.3em]"
              />
            </div>
            <button
              data-testid="change-pin-btn"
              disabled={changingPin || !currentPin || !newPin || !confirmPin}
              onClick={changePin}
              className="justify-self-start inline-flex items-center gap-2 bg-zinc-100 text-black hover:bg-white font-bold uppercase tracking-wide px-6 py-3 rounded-md disabled:opacity-50 disabled:pointer-events-none transition-colors"
            >
              <Lock className="h-4 w-4" /> {changingPin ? t("common.saving") : t("settings.change_pin_btn")}
            </button>
          </div>
        </div>

        <div className="pt-6 border-t border-zinc-800">
          <button
            data-testid="save-profile"
            disabled={saving}
            onClick={save}
            className="bg-cyan-400 text-black hover:bg-cyan-300 font-bold uppercase tracking-wide px-8 py-4 rounded-md disabled:opacity-60 transition-colors"
          >
            {saving ? t("common.saving") : t("settings.save_profile")}
          </button>
        </div>

        <div className="pt-6 border-t border-zinc-800">
          <h2 className="font-heading font-black text-2xl uppercase tracking-tight mb-1 flex items-center gap-2">
            <Bell className="h-5 w-5 text-cyan-400" /> {t("push.title")}
          </h2>
          <p className="text-sm text-zinc-500 mb-4 max-w-lg">{t("push.desc")}</p>

          {pushState === "unsupported" && <p className="text-sm text-zinc-500">{t("push.unsupported")}</p>}
          {pushState === "denied" && <p className="text-sm text-red-400">{t("push.denied")}</p>}
          {pushState === "off" && (
            <button disabled={pushBusy} onClick={enablePush}
              className="inline-flex items-center gap-2 bg-cyan-400 text-black hover:bg-cyan-300 font-bold uppercase tracking-wide px-6 py-3 rounded-md disabled:opacity-50 transition-colors">
              <Bell className="h-4 w-4" /> {pushBusy ? "…" : t("push.enable")}
            </button>
          )}
          {pushState === "on" && (
            <div className="flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-2 text-sm text-cyan-400"><Bell className="h-4 w-4" /> {t("push.enabled")}</span>
              <button disabled={pushBusy} onClick={disablePush}
                className="inline-flex items-center gap-2 bg-zinc-900 border border-zinc-800 hover:border-zinc-600 text-zinc-300 font-bold uppercase tracking-wide px-4 py-2.5 rounded-md text-sm disabled:opacity-50">
                <BellOff className="h-4 w-4" /> {t("push.disable")}
              </button>
            </div>
          )}
        </div>

        <div className="pt-6 mt-6 border-t border-red-500/20">
          <h2 className="font-heading font-black text-2xl uppercase tracking-tight mb-1 flex items-center gap-2 text-red-400">
            <AlertTriangle className="h-5 w-5" /> {t("danger.title")}
          </h2>
          <p className="text-sm text-zinc-500 mb-4 max-w-lg">{t("danger.warn")}</p>

          {dangerStep === 0 && (
            <button onClick={() => { setDangerStep(1); setDeletePin(""); }}
              className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-500 text-[#fff] font-bold uppercase tracking-wide px-6 py-3 rounded-md transition-colors">
              <Trash2 className="h-4 w-4" /> {t("danger.start")}
            </button>
          )}

          {dangerStep === 1 && (
            <div className="grid gap-3 max-w-sm">
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-400">{t("danger.pin_label")}</label>
              <input type="password" inputMode="numeric" value={deletePin} autoFocus
                onChange={(e) => setDeletePin(onlyDigits(e.target.value))}
                className="w-full bg-zinc-950 border border-red-500/40 focus:border-red-500 outline-none rounded-md px-4 py-3 text-lg tracking-[0.3em]" />
              <div className="flex gap-2">
                <button onClick={() => setDangerStep(0)}
                  className="px-4 py-2.5 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-300 font-bold uppercase tracking-wide text-sm hover:border-zinc-600">
                  {t("danger.cancel")}
                </button>
                <button disabled={deletePin.length < 4} onClick={() => setDangerStep(2)}
                  className="px-4 py-2.5 rounded-md bg-red-600 hover:bg-red-500 disabled:opacity-40 text-[#fff] font-bold uppercase tracking-wide text-sm">
                  {t("danger.next")}
                </button>
              </div>
            </div>
          )}

          {dangerStep === 2 && (
            <div className="max-w-sm bg-red-500/5 border border-red-500/30 rounded-md p-4 space-y-3">
              <div className="font-heading font-black text-lg uppercase tracking-tight text-red-400">{t("danger.sure_title")}</div>
              <p className="text-sm text-zinc-400">{t("danger.sure_text")}</p>
              <div className="flex gap-2">
                <button onClick={() => setDangerStep(0)}
                  className="flex-1 px-4 py-2.5 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-300 font-bold uppercase tracking-wide text-sm hover:border-zinc-600">
                  {t("danger.cancel")}
                </button>
                <button disabled={deleting} onClick={deleteAccount}
                  className="flex-1 px-4 py-2.5 rounded-md bg-red-600 hover:bg-red-500 disabled:opacity-50 text-[#fff] font-bold uppercase tracking-wide text-sm">
                  {deleting ? "…" : t("danger.confirm")}
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
